import { Box, Typography } from '@mui/material';
import { useApiCall } from 'redux/utils/api-hook';
import { useEffect } from 'react';
import { hospitalizationsRequests } from 'redux/requests/hospitalizations.requests';
import { useForm } from 'react-hook-form';
import { HospitalizationFormData } from '../types/hospitalizationFormData.types';
import {
  getHospitalizationFilterOptions,
  hospitalizationsSliceSelectors,
} from 'redux/slices/hospitalizations.slice';
import { useAppDispatch } from 'redux/store-hooks';
import FilterSelect from 'react-components/member-index/filter-select';
import { AppHeaderPortal, FlexContainer } from '@act/storybook';
import HospitalizationMetricItem from './HospitalizationMetricItem';
import HospitalizationsTable from './HospitalizationsTable';

const HospitalizationsPage = () => {
  const dispatch = useAppDispatch();
  const [metaCountResponse, _metaCountLoading, getMetaCount] = useApiCall(
    hospitalizationsRequests.getMetaCounts
  );
  const filterOptions =
    hospitalizationsSliceSelectors.useSelectAllHospitalizationFilterOptions();
  const filterOptionsStatus =
    hospitalizationsSliceSelectors.useSelectHospitalizationFilterOptionsStatus();

  useEffect(() => {
    getMetaCount({});
  }, []);

  const { control, watch, setValue } = useForm<HospitalizationFormData>({
    defaultValues: {
      specialFilter: null,
      clientIds: null,
      guideIds: null,
      statusIds: null,
      notAttempted: false,
      notOffered: false,
    },
  });

  const {
    notAttempted,
    notOffered,
    specialFilter,
    clientIds,
    guideIds,
    statusIds,
  } = watch();

  useEffect(() => {
    if (filterOptionsStatus === 'initial') {
      dispatch(getHospitalizationFilterOptions({}));
    }
  }, [filterOptionsStatus]);

  useEffect(() => {
    if (notAttempted) {
      setValue('notOffered', false);
      setValue('specialFilter', 'not-attempted');
    }
  }, [notAttempted]);

  useEffect(() => {
    if (notOffered) {
      setValue('notAttempted', false);
      setValue('specialFilter', 'not-offered');
    }
  }, [notOffered]);

  useEffect(() => {
    if (!notAttempted && !notOffered) {
      setValue('specialFilter', null);
    }
  }, [notAttempted, notOffered]);

  return (
    <>
      <FlexContainer
        direction="column"
        gap={4}
        height="100%"
        sx={{ overflow: 'auto' }}
      >
        <FlexContainer component="form" direction="column" height="fit-content">
          <FlexContainer direction="column" gap={2} height="fit-content">
            <Typography component="p" variant="titleMedium">
              Last updated:{' '}
              {new Date(
                metaCountResponse?.lastUpdatedAt ?? 0
              ).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
            <FlexContainer
              direction="row"
              gap={2}
              height="fit-content"
              width="100%"
            >
              <HospitalizationMetricItem
                control={control}
                count={metaCountResponse?.notAttempted ?? 0}
                metricName="Members not attempted"
                iconName="Users"
                fieldName="notAttempted"
                flex={3}
              />
              <HospitalizationMetricItem
                control={control}
                count={metaCountResponse?.notOffered ?? 0}
                metricName="Members not offered"
                iconName="Stethoscope"
                fieldName="notOffered"
                flex={2}
              />
            </FlexContainer>
          </FlexContainer>
          <FlexContainer
            direction="row"
            gap={2}
            height="fit-content"
            sx={{ maxWidth: '900px' }}
          >
            {filterOptions?.map(f => {
              return (
                <Box key={f.name} sx={{ flex: '0 1 33.33%' }}>
                  <FilterSelect filter={f} control={control} />
                </Box>
              );
            })}
          </FlexContainer>
        </FlexContainer>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <HospitalizationsTable
            specialFilter={specialFilter}
            clientIds={clientIds}
            guideIds={guideIds}
            statusIds={statusIds}
          />
        </Box>
      </FlexContainer>
      <AppHeaderPortal>
        <Box padding="0 16px">
          <Typography color="primary" variant="titleLarge">
            Hospitalizations
          </Typography>
        </Box>
      </AppHeaderPortal>
    </>
  );
};

export default HospitalizationsPage;
